import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
// @mui
import {
  Badge,
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  ListItemAvatar,
  ListItemButton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
// components
import { CustomAvatar } from '../../../components/custom-avatar';
import Iconify from '../../../components/iconify';
import Label from '../../../components/label';
import Scrollbar from '../../../components/scrollbar';
// services
import { useGetNavigationConfig } from '../../../services/auth/authServices';
import { useGetEnquiryNotifications } from '../../../services/notifications/notificationsServices';
import { fToNow } from '../../../utils/formatTime';
import { convertToFormattedString } from '../../../utils/helper';

// ----------------------------------------------------------------------

const TAB_COLORS = ['primary', 'info', 'warning', 'success', 'secondary', 'error'];

const moduleSlug = (path = '') => path.split('/').filter(Boolean).pop() || '';

export default function NotificationsDrawer() {
  const [open, setOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState('all');
  const { data, isLoading, isError } = useGetEnquiryNotifications();
  const { data: navConfig, isLoading: navLoading, isError: navError } = useGetNavigationConfig();

  const notifications = data?.data || [];
  const counts = data?.counts || {};
  const totalUnRead = counts.total || 0;

  const enquiryItems = useMemo(() => {
    const groups = Array.isArray(navConfig?.navigation) ? navConfig.navigation : [];
    return groups
      .filter((group) => group?.subheader === 'INBOX' || group?.subheader === 'ENQUIRIES')
      .flatMap((group) => group.items || [])
      .map((item) => ({
        ...item,
        slug: moduleSlug(item.path),
      }));
  }, [navConfig]);

  const tabs = [
    { value: 'all', label: 'All', count: totalUnRead, color: 'default' },
    ...enquiryItems.map((item, index) => ({
      value: item.slug,
      label: item.title,
      count: counts[item.slug] || 0,
      color: TAB_COLORS[index % TAB_COLORS.length],
    })),
  ];

  const visibleNotifications = notifications.filter((item) =>
    currentTab === 'all' ? true : item.module === currentTab
  );

  return (
    <>
      <IconButton aria-label="Notifications" onClick={() => setOpen(true)}>
        <Badge badgeContent={totalUnRead} color="error">
          <Iconify width={24} icon="solar:bell-bing-bold-duotone" />
        </Badge>
      </IconButton>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: 280, sm: 460 },
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 68, display: 'flex', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Notifications
          </Typography>
          <IconButton onClick={() => setOpen(false)} sx={{ display: { xs: 'inline-flex', sm: 'none' } }}>
            <Iconify icon="mingcute:close-line" />
          </IconButton>
        </Box>

        <Tabs
          value={tabs.some((tab) => tab.value === currentTab) ? currentTab : 'all'}
          onChange={(event, value) => setCurrentTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          TabIndicatorProps={{ children: <span /> }}
          sx={{
            minHeight: 48,
            flexShrink: 0,
            bgcolor: 'background.neutral',
            '& .MuiTabs-scroller': { p: 0.5 },
            '& .MuiTabs-flexContainer': { gap: 0 },
            '& .MuiTabs-scrollButtons': {
              width: 32,
              borderRadius: 1,
            },
            '& .MuiTabs-indicator': {
              py: 0.5,
              height: 1,
              bgcolor: 'transparent',
              '& > span': {
                width: 1,
                height: 1,
                display: 'block',
                borderRadius: 1,
                bgcolor: 'common.white',
                boxShadow: (theme) => theme.customShadows.z1,
              },
            },
            '& .MuiTab-root': {
              zIndex: 1,
              py: 1,
              px: 1.5,
              minHeight: 40,
              minWidth: 'auto',
              width: 'auto',
              maxWidth: 'none',
              flexGrow: 0,
              flexShrink: 0,
              flexWrap: 'nowrap',
              whiteSpace: 'nowrap',
              gap: 1,
              opacity: 1,
              marginRight: '0 !important',
              fontSize: 14,
              fontWeight: 600,
              lineHeight: 1.5,
              textTransform: 'none',
              color: 'text.secondary',
              '&.Mui-selected': {
                color: 'text.primary',
              },
            },
          }}
        >
          {tabs.map((tab) => {
            const isActive = tab.value === 'all' || tab.value === currentTab;

            return (
              <Tab
                key={tab.value}
                value={tab.value}
                label={tab.label}
                iconPosition="end"
                icon={
                  <Label
                    variant={isActive ? 'filled' : 'soft'}
                    color={tab.color}
                    sx={{
                      height: 22,
                      minWidth: 22,
                      px: 0.75,
                      borderRadius: 0.75,
                      ...(isActive &&
                        tab.color === 'default' && {
                          color: 'common.white',
                          bgcolor: 'text.primary',
                        }),
                    }}
                  >
                    {tab.count}
                  </Label>
                }
              />
            );
          })}
        </Tabs>

        <Box sx={{ flex: 1, minHeight: 0 }}>
          {isLoading || navLoading ? (
            <Box sx={{ height: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : null}

          {!isLoading && !navLoading && (isError || navError) ? (
            <Typography align="center" color="text.secondary" sx={{ p: 3 }}>
              Unable to load notifications. Please try again.
            </Typography>
          ) : null}

          {!isLoading && !navLoading && !isError && !navError ? (
            <Scrollbar sx={{ height: 1 }}>
              {visibleNotifications.length === 0 ? (
                <Typography align="center" color="text.secondary" sx={{ p: 3 }}>
                  No new enquiries
                </Typography>
              ) : (
                <Box component="ul" sx={{ p: 0, m: 0 }}>
                  {visibleNotifications.map((notification) => (
                    <Box component="li" key={`${notification.module}-${notification.id}`} sx={{ display: 'flex' }}>
                      <NotificationItem notification={notification} onClose={() => setOpen(false)} />
                    </Box>
                  ))}
                </Box>
              )}
            </Scrollbar>
          ) : null}
        </Box>
      </Drawer>
    </>
  );
}

NotificationItem.propTypes = {
  notification: PropTypes.shape({
    url: PropTypes.string,
    name: PropTypes.string,
    type: PropTypes.string,
    createdAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
  }).isRequired,
  onClose: PropTypes.func,
};

function NotificationItem({ notification, onClose }) {
  return (
    <ListItemButton
      component={RouterLink}
      to={notification.url || '#'}
      onClick={onClose}
      sx={{
        p: 2.5,
        width: 1,
        alignItems: 'flex-start',
        borderBottom: (theme) => `dashed 1px ${theme.palette.divider}`,
        position: 'relative',
      }}
    >
      <Box
        sx={{
          top: 26,
          right: 20,
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: 'info.main',
          position: 'absolute',
        }}
      />
      <ListItemAvatar>
        <CustomAvatar name={notification.name} alt={notification.name} />
      </ListItemAvatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="subtitle2" component="span" sx={{ mr: 0.5 }}>
          {notification.name}
        </Typography>
        <Typography variant="body2" component="span">
          has submitted an enquiry.
        </Typography>
        <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="body2" sx={{ color: 'primary.main' }} noWrap>
            {convertToFormattedString(notification.type)}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <Iconify icon="eva:clock-fill" width={16} sx={{ mr: 0.5, color: 'text.disabled' }} />
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              {fToNow(notification.createdAt)}
            </Typography>
          </Box>
        </Box>
      </Box>
    </ListItemButton>
  );
}
